import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import {
  Controller,
  FormProvider,
  useForm,
  useFieldArray,
} from "react-hook-form";

import moment from "moment";
import { isEmpty } from "lodash";

import {
  faCheck,
  faTimes,
  faArrowRight,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  Button,
  Checkbox,
  TextInput,
  RadioButtonGroup,
  RadioButton,
  Select,
  SelectItem,
  Blockquote,
  Loading,
} from "@wfp/ui";

import useStore from "helpers/useStore";
import {
  buildAttendanceUrl,
  getFilteredAttendanceTotal,
  getPreviousAttendanceStoreWithAttendanceDataByDate,
} from "helpers/attendance";
import { getStockStatus } from "helpers/stock";

import {
  requestCommoditySourcesForSchool,
  setCommoditySourcesFromSchoolCache,
  requestAllCommoditySources,
  getAllCommoditySources,
  getAllCommoditySourcesFetching,
  getCurrentSchoolProfile,
  getCurrentSchoolProfileWfpCommodities,
  getIsSchoolsFetching,
  getCurrentSchoolProfileCommodities,
} from "data-handler/ducks/schools";
import commoditiesDuck from "data-handler/ducks/commodities";
import {
  updateStore,
  getDeliveryAttendancesByDateRange,
  getStudentAttendancesByDateRange,
  getCurrentOrLatestSchoolYear,
  getConsumptionNoMealReasons,
  Attendance,
  AttendanceConsumption,
  NewStockEvent,
} from "data-handler/ducks/stores";

import ModalExtended from "components/ModalExtended";
import { currentSchoolYearDateValidation } from "components/IncidentEdit";
import { RepeaterItem } from "components/Repeater";
import CommoditySelect from "components/CommoditySelect";
import QuantityInput from "components/QuantityInput";
import RepeaterAddButton from "components/RepeaterAddButton";
import ErrorList from "components/ErrorList";
import DateInput from "components/DateInput";
import { getSchoolDays } from "components/Report/ReportModalClose";
import ButtonRouter from "components/ButtonRouter";

import {
  purchaseDetailCategory,
  deliveryCategory,
  takeHomeRationCategory,
  nonWfpCategory,
} from "SCConstants";

import "./_meals-edit.scss";
import { CommoditySource } from "data-handler/ducks/schools2";
import { getLoggedInOffline } from "data-handler/ducks/auth";
import { getFoodSources } from "data-handler/ducks/foodSources";
import {
  getNoMealReasons,
  NoMealReason,
} from "data-handler/ducks/noMealReasons";

export const ERROR_MESSAGES = {
  missingCategory: (
    <FormattedMessage
      id="MealsEdit.missingCategory"
      defaultMessage="Select a category"
    />
  ),
  insufficientStock: (
    <FormattedMessage
      id="MealsEdit.insufficientStock"
      defaultMessage="The meal quantity exceeds the stock for this commodity, please reduce."
    />
  ),
  missingCommodity: (
    <FormattedMessage
      id="MealsEdit.missingCommodity"
      defaultMessage="Select a commodity"
    />
  ),
  missingQuantity: (
    <FormattedMessage
      id="MealsEdit.missingQuantity"
      defaultMessage="Enter a quantity"
    />
  ),
  zeroQuantity: (
    <FormattedMessage
      id="MealsEdit.zeroQuantity"
      defaultMessage="Please add a quantity to all commodities"
    />
  ),
  negativeQuantity: (
    <FormattedMessage
      id="MealsEdit.negativeQuantity"
      defaultMessage="Quantity cannot be negative"
    />
  ),
};

export const requiredLabelCommodities = (
  <div
    className="wfp--form-required"
    style={{ borderColor: " 2px solid #C5192D" }}
  >
    {ERROR_MESSAGES["missingCommodity"]}
  </div>
);

const missingCategoryRequiredMessage = (
  <FormattedMessage
    id="MealsEdit.missingCategoryStar"
    defaultMessage="* Select a category"
  />
);

type FormStockEvent = NewStockEvent & { quantityPerStudent?: string };

type FormValues = {
  no_meal_reasons: boolean[];
  other_no_meal_reason?: string;
  stock_events: FormStockEvent[];
  replicate_from_date?: string;
  replicate_to_date?: string;
};

type FormStoreInput = Attendance & { isPreviousStore?: boolean };

const targetFromCategory = (category: string): "DEL" | "PUR" => {
  switch (category) {
    case purchaseDetailCategory:
      return "PUR";
    case deliveryCategory:
    case nonWfpCategory:
    case takeHomeRationCategory:
      return "DEL";
    default:
      return "DEL";
  }
};

/**
 * Ensure a row carries the constant CONS-event metadata (sign/type/target/meta).
 * Existing rows from the BE already have these; new rows added via the form do not.
 */
const enrichStockEvent = (stockEvent: FormStockEvent): NewStockEvent => {
  const category =
    stockEvent.category || stockEvent.meta?.category || deliveryCategory;
  const { quantityPerStudent: _qps, ...rest } = stockEvent;
  return {
    ...rest,
    category,
    sign: -1,
    type: "CONS",
    target: targetFromCategory(category),
    side_effects: stockEvent.side_effects || [],
    meta: { ...(stockEvent.meta || {}), category },
  };
};

export const storeToFormValues = (
  store: FormStoreInput,
  prepopulatePerStudent: boolean,
  totalStudents: number
): FormValues => {
  const consumption = store.consumption
    ? (JSON.parse(JSON.stringify(store.consumption)) as AttendanceConsumption)
    : ({} as AttendanceConsumption);

  const stockEvents: FormStockEvent[] = (consumption.stock_events || []).map(
    (se) => ({
      ...se,
      source:
        typeof se.source === "object" ? (se.source as any)?.id : se.source,
    })
  );

  if (
    prepopulatePerStudent &&
    store.isPreviousStore &&
    store.levels &&
    stockEvents.length > 0 &&
    totalStudents > 0
  ) {
    let attendance = 0;
    store.levels.forEach((level) => {
      attendance += parseFloat((level.female as unknown) as string);
      attendance += parseFloat((level.male as unknown) as string);
    });

    stockEvents.forEach((stockEvent) => {
      if (attendance > 0) {
        const quantityPerStudent = parseFloat(stockEvent.quantity) / attendance;
        stockEvent.quantityPerStudent = quantityPerStudent.toFixed(3);
        stockEvent.quantity = (quantityPerStudent * totalStudents).toFixed(3);
      }
    });
  }

  const no_meal_reasons: boolean[] = [];
  const sourceNoMealReasons = getConsumptionNoMealReasons(consumption);
  sourceNoMealReasons.forEach((reasonId: number) => {
    no_meal_reasons[reasonId] = true;
  });

  return {
    stock_events: stockEvents,
    no_meal_reasons,
    other_no_meal_reason:
      consumption.meta?.other_no_meal_reason ||
      consumption.other_no_meal_reason ||
      "",
  };
};

export const formValuesToStore = (values: FormValues) => {
  const no_meal_reason_ids: number[] = [];
  (values.no_meal_reasons || []).forEach((reason, i) => {
    if (reason) {
      no_meal_reason_ids.push(i);
    }
  });

  const stock_events = (values.stock_events || []).map(enrichStockEvent);

  return {
    stock_events,
    no_meal_reasons: no_meal_reason_ids,
    other_no_meal_reason: values.other_no_meal_reason || "",
    meta: {
      other_no_meal_reason: values.other_no_meal_reason || "",
    },
  };
};

export const isNoMealReasonDisabled = (
  option: NoMealReason,
  attendanceProvided: boolean,
  studentAttendanceProvided: boolean,
  studentAttendanceEnabled: boolean
) => {
  if (studentAttendanceEnabled) {
    const noSchoolWithAttendanceOrMeal =
      option.is_no_school_day &&
      (attendanceProvided || studentAttendanceProvided);
    const noMealWithMeal = !option.is_no_school_day && attendanceProvided;
    return noMealWithMeal || noSchoolWithAttendanceOrMeal;
  }
  return option.is_no_school_day && attendanceProvided;
};

type CommodityCategory =
  | typeof deliveryCategory
  | typeof takeHomeRationCategory
  | typeof purchaseDetailCategory
  | typeof nonWfpCategory;

/** Handles validation for ALL form values */
export const validationResolver = (
  values: any,
  {
    currentStoreData,
    currentSchoolEnabledPurchases,
    currentSchoolEnabledTHR,
    stockStatus,
  }: any
) => {
  const errors: {
    [key: string]: { message: JSX.Element; requiredMessage?: JSX.Element };
  } = {};
  if (values.stock_events) {
    values.stock_events.forEach((commodity: FormStockEvent, i: number) => {
      const commodityCategory = commodity.category as CommodityCategory;
      const commodityId = commodity.commodity;
      // Do not display insufficient stock for non-WFP category
      // (doesn't apply to purchases with non-wfp commodities)
      if (commodityCategory && commodityCategory !== nonWfpCategory) {
        let currentStock =
          Number(stockStatus[commodityCategory]?.[commodityId as any]) || 0;

        const previousStockEvent =
          currentStoreData?.consumption?.stock_events?.[i];
        if (
          previousStockEvent !== undefined &&
          commodity.commodity === previousStockEvent.commodity &&
          previousStockEvent.quantity !== undefined
        ) {
          currentStock += parseFloat(previousStockEvent.quantity);
        }
        if (
          parseFloat(commodity.quantity) > currentStock &&
          commodity.commodity
        ) {
          errors[`stock_events[${i}].quantity`] = {
            message: ERROR_MESSAGES["insufficientStock"],
          };
        }
        if (
          (currentSchoolEnabledPurchases || currentSchoolEnabledTHR) &&
          !commodity.category
        ) {
          errors["category"] = {
            message: ERROR_MESSAGES["missingCategory"],
            requiredMessage: missingCategoryRequiredMessage,
          };
        }
      }

      if (!commodity.commodity) {
        errors[`stock_events[${i}].commodity`] = {
          message: ERROR_MESSAGES["missingCommodity"],
        };
      }
      // `quantity` is required on each commodity row
      if (!commodity.quantity) {
        errors[`stock_events[${i}].quantity`] = {
          message: ERROR_MESSAGES["missingQuantity"],
        };
      }
      if (
        parseFloat(commodity.quantity) === 0 &&
        commodity.commodity &&
        commodity.quantity
      ) {
        errors[`stock_events[${i}].quantity`] = {
          message: ERROR_MESSAGES["zeroQuantity"],
        };
      }
      if (
        parseFloat(commodity.quantity) < 0 &&
        commodity.commodity &&
        commodity.quantity
      ) {
        errors[`stock_events[${i}].quantity`] = {
          message: ERROR_MESSAGES["negativeQuantity"],
        };
      }
    });
  }
  return {
    values: !isEmpty(errors) ? {} : values,
    errors,
  };
};

const makeCategoryOption = (category: CommoditySource) => ({
  value: category.code,
  name: category.code,
  label: category.name,
});

const makeCategoryOptions = (
  allCommoditySources: CommoditySource[],
  deliveryInStock: boolean,
  currentSchoolEnabledPurchases: boolean,
  currentSchoolEnabledTHR: boolean
) => {
  if (allCommoditySources.length === 0) {
    return [];
  }

  let allCommoditySourcesMap: {
    [categoryCode: string]: CommoditySource;
  } = {};
  for (let source of allCommoditySources) {
    allCommoditySourcesMap[source.code] = source;
  }

  let categoryOptionCodes = [];
  if (deliveryInStock) {
    categoryOptionCodes.push(deliveryCategory);
  }
  if (currentSchoolEnabledPurchases) {
    categoryOptionCodes.push(purchaseDetailCategory);
  }
  if (currentSchoolEnabledTHR) {
    categoryOptionCodes.push(takeHomeRationCategory);
  }

  // TODO: Change this when the feature flag for contributions is added
  categoryOptionCodes.push(nonWfpCategory);

  let commoditySources = categoryOptionCodes.map(
    (code: string) => allCommoditySourcesMap[code]
  );
  commoditySources.sort((a, b) => a.order - b.order);

  return commoditySources.map(makeCategoryOption);
};

const MealsEditModal = React.memo(
  ({
    currentStoreData,
    currentStudentStoreData,
    previousAttendanceStoreWithConsumptionData,
    prepopulatePerStudent,
    prepopulateTotalQuantity,
    commoditiesIndex,
    latestStockStatus,
    currentSchoolProfile,
    commoditiesListWfpFromSchoolProfile,
    commoditiesListFromSchoolProfile,
    latestSchoolYear,
    attendances,
    studentAttendances,
    allCommoditySources,
  }: any) => {
    const deliveryInStock = useMemo(
      () =>
        commoditiesListWfpFromSchoolProfile.some(
          (commodity: { id: number }) =>
            Number(latestStockStatus.delivery[commodity.id] || "0") > 0
        ),
      [commoditiesListWfpFromSchoolProfile, latestStockStatus.delivery]
    );
    // There are thr wfp commodities that have Nonzero values.
    const thrInStock = useMemo(
      () =>
        commoditiesListWfpFromSchoolProfile.some(
          (commodity: { id: number }) =>
            Number(latestStockStatus.takehomeration[commodity.id] || "0") > 0
        ),
      [commoditiesListWfpFromSchoolProfile, latestStockStatus.takehomeration]
    );
    // There are purchasedetail wfp commodities that have Nonzero values.
    const purchaseInStock = useMemo(
      () =>
        commoditiesListFromSchoolProfile.some(
          (commodity: { id: number }) =>
            Number(latestStockStatus.purchasedetail[commodity.id] || "0") > 0
        ),
      [commoditiesListFromSchoolProfile, latestStockStatus.purchasedetail]
    );

    const currentSchoolEnabledPurchases =
      purchaseInStock || currentSchoolProfile.enable_cash_purchases;
    const currentSchoolEnabledTHR =
      thrInStock && currentSchoolProfile.enable_take_home_rations;
    const currentSchoolEnabledStudentAttendance =
      currentSchoolProfile.enable_student_attendance;

    const { submitBtnRef, submitViaBtnRefClick } = useStore();

    const dispatch = useDispatch();

    const categoryOptions = useMemo(
      () =>
        makeCategoryOptions(
          allCommoditySources,
          deliveryInStock,
          currentSchoolEnabledPurchases,
          currentSchoolEnabledTHR
        ),
      [
        allCommoditySources,
        deliveryInStock,
        currentSchoolEnabledPurchases,
        currentSchoolEnabledTHR,
      ]
    );

    const params: any = useParams();
    const history = useHistory();
    const isTHRMeal = params.details === "new-take-home-ration-data";

    const id = (currentStoreData && currentStoreData.client_id) || undefined;

    const newDate = useMemo(
      () =>
        currentStoreData?.occurred_on
          ? moment(currentStoreData?.occurred_on)
          : params.item && params.item.split("+").length === 2
          ? moment(params.item.split("+")[1])
          : undefined,
      [currentStoreData, params.item]
    );

    const currentDate: string = newDate!.format("YYYY-MM-DD");

    const attendanceProvided =
      currentStoreData?.levels && currentStoreData?.type !== "delete"
        ? true
        : false;

    const studentAttendanceProvided = !!(
      currentStudentStoreData && currentStudentStoreData?.type !== "delete"
    );

    const isReasonDisabled = useCallback(
      (option: NoMealReason) =>
        isNoMealReasonDisabled(
          option,
          attendanceProvided,
          studentAttendanceProvided,
          currentSchoolEnabledStudentAttendance
        ),
      [
        currentSchoolEnabledStudentAttendance,
        attendanceProvided,
        studentAttendanceProvided,
      ]
    );

    let totalStudents = useMemo(
      () =>
        getFilteredAttendanceTotal({
          attendance: currentStoreData,
        }),
      [currentStoreData]
    );

    const totalPrevStore = useSelector(
      getPreviousAttendanceStoreWithAttendanceDataByDate(moment(newDate))
    );

    const noMealReasons = useSelector(getNoMealReasons);

    const totalPrevStudents = useMemo(
      () =>
        getFilteredAttendanceTotal({
          attendance: totalPrevStore,
        }),
      [totalPrevStore]
    );

    if (!attendanceProvided) {
      totalStudents = totalPrevStudents;
    }

    const foodSources = useSelector(getFoodSources);

    const stockStatus = useSelector(
      getStockStatus(moment(newDate).format("YYYY-MM-DD"), false, false)
    );

    const defaultValues = useMemo(
      () =>
        storeToFormValues(
          previousAttendanceStoreWithConsumptionData &&
            ((prepopulatePerStudent &&
              previousAttendanceStoreWithConsumptionData.levels) ||
              prepopulateTotalQuantity)
            ? {
                ...previousAttendanceStoreWithConsumptionData,
                isPreviousStore: true,
              }
            : currentStoreData,
          prepopulatePerStudent,
          totalStudents
        ),
      [
        previousAttendanceStoreWithConsumptionData,
        prepopulatePerStudent,
        prepopulateTotalQuantity,
        currentStoreData,
        totalStudents,
      ]
    );
    const methods = useForm({
      resolver: validationResolver,
      defaultValues: defaultValues,
      context: {
        currentStoreData,
        currentSchoolEnabledPurchases,
        currentSchoolEnabledTHR,
        stockStatus,
      },
    });

    const {
      control,
      handleSubmit,
      watch,
      errors,
      setValue,
      register,
    } = methods;

    const { fields, append, remove } = useFieldArray({
      control,
      name: "stock_events",
    });
    const maxOtherReasonCharacters = 200;

    const otherReason = watch("other_no_meal_reason");

    const isOtherReasonInputted =
      otherReason !== undefined && otherReason?.length > 0;

    const isNoMealReasonsChecked =
      watch("no_meal_reasons").includes(true) || isOtherReasonInputted;

    const stockEventsWatch = watch("stock_events");
    const categoryWatch = (category: string, index: number) =>
      watch(`stock_events[${index}].category`) === category ? true : false;
    const [meal_provided, _setMealProvided] = useState(
      currentStoreData.consumption?.meal_provided ??
        (prepopulateTotalQuantity || prepopulatePerStudent
          ? previousAttendanceStoreWithConsumptionData?.consumption
              ?.meal_provided
          : undefined)
    );

    const currentSchoolYearDate = useMemo(
      () => currentSchoolYearDateValidation(latestSchoolYear!),
      [latestSchoolYear]
    );

    const setMealProvided = useCallback(
      (value: any) => {
        if (value === true) {
          // Ensure at least one commodity row
          if (fields.length === 0) {
            append({});
          }
        } else if (value === false) {
          // Remove all commodity rows
          remove(); // Removes all when no argument
        }
        _setMealProvided(value);
      },
      [fields.length, append, remove]
    );

    useEffect(() => {
      if (prepopulatePerStudent || prepopulateTotalQuantity) {
        setMealProvided(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prepopulatePerStudent, prepopulateTotalQuantity]);

    const closeModal = useCallback(
      (newId: string) => {
        history.push(
          buildAttendanceUrl(
            currentDate,
            params.schoolId,
            newId ? newId : id,
            params.thrItem,
            params.studentItem
          )
        );
      },
      [
        currentDate,
        params.schoolId,
        id,
        params.thrItem,
        params.studentItem,
        history,
      ]
    );

    const replicateNoMealServed = useCallback(
      (values: any) => {
        // Get the school days for the selected period
        const schoolDays = getSchoolDays(
          values.replicate_from_date,
          values.replicate_to_date,
          latestSchoolYear?.weekdays
        );

        const selectedNoMealReasons = noMealReasons.filter(
          (option) => values.no_meal_reasons?.[option.id]
        );

        // Go through all school days and create/edit the attendance objects accordingly:
        // - do not create/edit for the current day
        // - do not create/edit for existing attendance with consumption already entered
        // - do not create/edit for days where a selected reason would not be allowed,
        //   e.g. a no-school day reason on a day with attendance already entered
        schoolDays
          .filter((day) => !moment(day).isSame(newDate))
          .forEach((day) => {
            const attendanceStoreObject = attendances.find(
              (item: { occurred_on: string }) => item.occurred_on === day
            );
            const dayAttendanceProvided =
              (attendanceStoreObject?.levels || []).length > 0 &&
              attendanceStoreObject?.type !== "delete";
            const dayStudentAttendanceProvided = studentAttendances.some(
              (item: { occurred_on: string; type?: string }) =>
                item.occurred_on === day && item.type !== "delete"
            );
            const hasDisabledReason = selectedNoMealReasons.some((option) =>
              isNoMealReasonDisabled(
                option,
                dayAttendanceProvided,
                dayStudentAttendanceProvided,
                currentSchoolEnabledStudentAttendance
              )
            );
            if (
              !hasDisabledReason &&
              (!attendanceStoreObject ||
                (attendanceStoreObject && !attendanceStoreObject.consumption))
            ) {
              const attendanceValues = attendanceStoreObject
                ? { ...attendanceStoreObject }
                : {};
              const consumptionValues = attendanceStoreObject?.consumption
                ? { ...attendanceStoreObject.consumption }
                : {};
              dispatch(
                updateStore({
                  id: attendanceStoreObject?.client_id || undefined,
                  values: {
                    ...attendanceValues,
                    occurred_on: day,
                    consumption: {
                      ...consumptionValues,
                      ...formValuesToStore(values),
                      event_type: "consumption",
                      occurred_at: day,
                      meal_provided,
                      category: deliveryCategory,
                    },
                  },
                  model: "attendance",
                  type:
                    (attendanceStoreObject?.client_id && "update") || "create",
                  section: "consumption",
                  category: deliveryCategory,
                })
              );
            }
          });
      },
      [
        latestSchoolYear,
        newDate,
        attendances,
        studentAttendances,
        noMealReasons,
        currentSchoolEnabledStudentAttendance,
        meal_provided,
        dispatch,
      ]
    );

    const onSubmit = useCallback(
      (values: any) => {
        if (
          values.replicate_from_date &&
          values.replicate_to_date &&
          meal_provided === false
        ) {
          replicateNoMealServed(values);
        }

        // Delete not needed fields
        delete values.replicate_from_date;
        delete values.replicate_to_date;

        const toBeSaved = {
          ...currentStoreData,
          consumption: {
            ...currentStoreData.consumption,
            ...formValuesToStore(values),
            event_type: "consumption",
            occurred_at:
              currentStoreData.consumption?.occurred_at ||
              currentStoreData.occurred_on,
            meal_provided,
            category: deliveryCategory,
          },
        };

        // Meals can either be served or not served.
        // If meals were served, delete data related to not serving meals, and viceversa.
        if (meal_provided === true) {
          delete toBeSaved.consumption.no_meal_reasons;
          delete toBeSaved.consumption.other_no_meal_reason;
          if (toBeSaved.consumption.meta) {
            delete toBeSaved.consumption.meta.other_no_meal_reason;
          }
        } else if (meal_provided === false) {
          delete toBeSaved.consumption.stock_events;
        }
        const store = dispatch(
          updateStore({
            id: (currentStoreData && currentStoreData.client_id) || undefined,
            values: toBeSaved,
            model: "attendance",
            type:
              (currentStoreData && currentStoreData.client_id && "update") ||
              "create",
            section: "consumption",
            category: deliveryCategory,
          })
        );

        if (!store.blocked) {
          closeModal(store.data.id);
        }
      },
      [
        meal_provided,
        replicateNoMealServed,
        currentStoreData,
        dispatch,
        closeModal,
      ]
    );

    const currentParam =
      params.details.split("-")[0] === "new" ? "new-meals" : "meals";

    return (
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalExtended
            open
            hideClose
            onRequestSubmit={submitViaBtnRefClick}
            onRequestClose={() => {}}
            onSecondarySubmit={() => closeModal(params.item)}
            modalLabel={
              <FormattedMessage
                id="MealsEdit.mealConsumptionLabel"
                defaultMessage="Meal consumption"
              />
            }
            className="meals-edit"
            modalHeading={
              <>
                {moment(currentStoreData.occurred_on).format(
                  "dddd, DD.MM.YYYY"
                )}{" "}
                {meal_provided === true && (
                  <Button
                    kind="danger"
                    icon={<FontAwesomeIcon icon={faTimesCircle} />}
                    onClick={() => setMealProvided(false)}
                  >
                    <FormattedMessage
                      id="MealsEdit.noMealsServed"
                      defaultMessage="No meals served"
                    />
                  </Button>
                )}
                {meal_provided === false && (
                  <Button onClick={() => setMealProvided(true)}>
                    <FormattedMessage
                      id="MealsEdit.mealsServedQuestion"
                      defaultMessage="Meal served?"
                    />
                  </Button>
                )}
              </>
            }
            modalFooter={
              !meal_provided
                ? (props: any) => (
                    <div className="modal-footer">
                      <div className="repeat-section">
                        <label className="wfp--label repeat-label">
                          <FormattedMessage
                            id="MealsEdit.repeatForOtherPeriod"
                            defaultMessage="Repeat for other period"
                          />
                        </label>
                        <Controller
                          className="date-from-picker"
                          as={
                            <DateInput
                              labelText={
                                <FormattedMessage
                                  id="MealsEdit.fromLabel"
                                  defaultMessage="From"
                                />
                              }
                              min={currentSchoolYearDate.min}
                              max={currentSchoolYearDate.max}
                            />
                          }
                          name="replicate_from_date"
                        />
                        <FontAwesomeIcon
                          className="arrow-right"
                          icon={faArrowRight}
                        />
                        <Controller
                          className="date-to-picker"
                          as={
                            <DateInput
                              labelText={
                                <FormattedMessage
                                  id="MealsEdit.toLabel"
                                  defaultMessage="To"
                                />
                              }
                              min={currentSchoolYearDate.min}
                              max={currentSchoolYearDate.max}
                            />
                          }
                          name="replicate_to_date"
                        />
                      </div>
                      <div className="wfp--modal__buttons-container">
                        {props.secondaryButtonText && (
                          <Button
                            kind="secondary"
                            disabled={props.secondaryButtonDisabled}
                            onClick={
                              props.onSecondarySubmit
                                ? props.onSecondarySubmit
                                : props.onRequestClose
                            }
                          >
                            {props.secondaryButtonText}
                          </Button>
                        )}
                        <Button
                          kind="primary"
                          disabled={props.primaryButtonDisabled}
                          onClick={props.onRequestSubmit}
                        >
                          {props.primaryButtonText}
                        </Button>
                      </div>
                    </div>
                  )
                : undefined
            }
            wide
            primaryButtonDisabled={!isNoMealReasonsChecked && !meal_provided}
            primaryButtonText={
              <FormattedMessage
                id="MealsEdit.saveMealButton"
                defaultMessage="Save"
              />
            }
            secondaryButtonText={
              <FormattedMessage
                id="MealsEdit.cancelButton"
                defaultMessage="Cancel"
              />
            }
            selectorPrimaryFocus={false}
            passiveModal={meal_provided !== true && meal_provided !== false}
          >
            <div>
              {meal_provided !== true && meal_provided !== false && !isTHRMeal && (
                <div className="meals-edit__question">
                  <h3>
                    <FormattedMessage
                      id="MealsEdit.wasAMealProvidedQuestion"
                      defaultMessage="Was a meal served?"
                    />
                  </h3>
                  <div>
                    <Button
                      type="button"
                      kind="secondary"
                      onClick={() => setMealProvided(false)}
                      icon={<FontAwesomeIcon icon={faTimes} />}
                    >
                      <FormattedMessage
                        id="MealsEdit.mealNotServedButton"
                        defaultMessage="No"
                      />
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setMealProvided(true)}
                      icon={<FontAwesomeIcon icon={faCheck} />}
                    >
                      <FormattedMessage
                        id="MealsEdit.mealProvidedButton"
                        defaultMessage="Meal served"
                      />
                    </Button>
                  </div>
                </div>
              )}
              {meal_provided === false && !isTHRMeal && (
                <div className="wfp--form wfp--form-long no-meal-reasons-container">
                  <RadioButtonGroup
                    name="MealsEdit.whyNoMealQuestion"
                    labelText={
                      <FormattedMessage
                        id="MealsEdit.whyNoMealQuestion"
                        defaultMessage="Why will no meal be served today?"
                      />
                    }
                    vertical
                  >
                    {noMealReasons.map((option) => (
                      <div key={option.id} className="no-meal-reasons-checkbox">
                        <Controller
                          render={({ onChange, value }) => (
                            <Checkbox
                              labelText={option.name}
                              id={`noMealReasons-${option.id}`}
                              checked={value}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => onChange(e.target.checked)}
                              disabled={isReasonDisabled(option)}
                            />
                          )}
                          name={`no_meal_reasons[${option.id}]`}
                        />
                        {isReasonDisabled(option) && (
                          <span className="tooltiptext">
                            <FormattedMessage
                              id="MealsEdit.noMealReasonsAttendance"
                              defaultMessage="Please remove entered attendance to choose this reason"
                            />
                          </span>
                        )}
                      </div>
                    ))}
                  </RadioButtonGroup>
                  <Controller
                    as={
                      <TextInput
                        maxLength={maxOtherReasonCharacters}
                        labelText={
                          <FormattedMessage
                            id="MealsEdit.otherReason"
                            defaultMessage="Other reason"
                          />
                        }
                      />
                    }
                    name={"other_no_meal_reason"}
                    defaultValue=""
                  />
                </div>
              )}
              {meal_provided === true && (
                <>
                  {attendanceProvided === false && totalStudents === 0 && (
                    <Blockquote kind="warning">
                      <FormattedMessage
                        id="info.noAttendance"
                        defaultMessage="You haven't inputted attendance data therefore we can't
                        populate the total quantity field according to the quantity
                        per student automatically."
                      />
                    </Blockquote>
                  )}
                  {attendanceProvided === false && totalStudents > 0 && (
                    <Blockquote kind="warning">
                      <FormattedMessage
                        id="editMeals.usingPreviousDaysAttendance"
                        defaultMessage="Since you haven't inputted attendance data for today,
                        the previous day's attendance will be used to populate the total
                        quantity field according to the quantity per student "
                      />
                    </Blockquote>
                  )}
                  <ErrorList errors={errors} />

                  <div>
                    {fields.map((field, index) => (
                      <RepeaterItem
                        key={field.id}
                        removeAction={() => remove(index)}
                        disableRemove={fields.length === 1}
                      >
                        <>
                          <RadioButtonGroup
                            name="MealsEdit.commoditySource"
                            labelText={
                              <FormattedMessage
                                id="MealsEdit.commoditySource"
                                defaultMessage="Commodity Source"
                              />
                            }
                          >
                            {categoryOptions.map((category) => (
                              <RadioButton
                                key={category.value}
                                labelText={category.label}
                                id={`stock_events[${index}][${category.value}]`}
                                name={`stock_events[${index}].category`}
                                value={category.value}
                                defaultChecked={
                                  category.value === categoryOptions[0]?.value
                                }
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                  setValue(
                                    `stock_events[${index}].category`,
                                    e.target.value,
                                    {
                                      shouldDirty: true,
                                    }
                                  );
                                  setValue(
                                    `stock_events[${index}].commodity`,
                                    null,
                                    {
                                      shouldDirty: true,
                                    }
                                  );
                                }}
                                ref={
                                  currentSchoolEnabledTHR ||
                                  currentSchoolEnabledPurchases
                                    ? register({ required: true })
                                    : register({ required: false })
                                }
                                other={{
                                  name: `stock_events[${index}].category`,
                                }}
                              />
                            ))}
                          </RadioButtonGroup>
                        </>
                        <Controller
                          render={({ onChange, value }) => (
                            <CommoditySelect
                              category={watch(
                                `stock_events[${index}].category`
                              )}
                              onChange={(e: {
                                value: string;
                                category: string;
                                measure_unit: unknown;
                              }) => {
                                setValue(
                                  `stock_events[${index}].measure_unit`,
                                  e.measure_unit,
                                  {
                                    shouldDirty: true,
                                  }
                                );
                                onChange(e.value, e.category);
                              }}
                              value={value}
                              showStock={true}
                              invalidOverride={
                                errors[`stock_events[${index}].commodity`] !==
                                undefined
                              }
                              invalidTextOverride={requiredLabelCommodities}
                              date={currentStoreData.occurred_on}
                            />
                          )}
                          name={`stock_events[${index}].commodity`}
                          control={control}
                        />
                        {
                          // Only show `source` field when selected commodity is not from WFP
                          stockEventsWatch &&
                            stockEventsWatch[index] &&
                            stockEventsWatch[index].commodity &&
                            !commoditiesIndex[stockEventsWatch[index].commodity]
                              ?.is_wfp &&
                            categoryWatch(nonWfpCategory, index) && (
                              <Controller
                                className="meals__source"
                                defaultValue={foodSources[0].id}
                                as={
                                  <Select
                                    labelText={
                                      <FormattedMessage
                                        id="MealsEdit.source"
                                        defaultMessage="Source"
                                      />
                                    }
                                  >
                                    {foodSources.map((foodSource) => (
                                      <SelectItem
                                        key={foodSource.id}
                                        value={foodSource.id}
                                        text={foodSource.source}
                                      />
                                    ))}
                                  </Select>
                                }
                                name={`stock_events[${index}].source`}
                                placeholder=""
                                control={control}
                              />
                            )
                        }
                        <Controller
                          render={({ onChange, onBlur, value }) => {
                            return (
                              <QuantityInput
                                control={control}
                                value={value}
                                onChange={(e) => {
                                  if (totalStudents) {
                                    setValue(
                                      `stock_events[${index}].quantity`,
                                      (e.target.value * totalStudents).toFixed(
                                        2
                                      ),
                                      { shouldDirty: true }
                                    );
                                  }
                                  return onChange(e);
                                }}
                                onBlur={onBlur}
                                labelText={
                                  <FormattedMessage
                                    id="MealsEdit.quantityPerStudents"
                                    defaultMessage="Quantity per student"
                                  />
                                }
                                setValue={setValue}
                                name={`stock_events[${index}].quantityPerStudent`}
                                invalid={
                                  errors[
                                    `stock_events[${index}].quantityPerStudent`
                                  ]
                                }
                                invalidText={
                                  errors[
                                    `stock_events[${index}].quantityPerStudent`
                                  ]?.message
                                }
                                measureUnit={watch(
                                  `stock_events[${index}].measure_unit`
                                )}
                              />
                            );
                          }}
                          control={control}
                          name={`stock_events[${index}].quantityPerStudent`}
                        />

                        <Controller
                          render={({ onChange, onBlur, value }) => {
                            return (
                              <QuantityInput
                                control={control}
                                value={value}
                                onChange={(e) => {
                                  if (totalStudents) {
                                    setValue(
                                      `stock_events[${index}].quantityPerStudent`,
                                      (e.target.value / totalStudents).toFixed(
                                        2
                                      ),
                                      { shouldDirty: true }
                                    );
                                  }
                                  return onChange(e);
                                }}
                                onBlur={onBlur}
                                labelText={
                                  <FormattedMessage
                                    id="MealsEdit.TotalQuantity"
                                    defaultMessage="Total quantity"
                                  />
                                }
                                setValue={setValue}
                                name={`stock_events[${index}].quantity`}
                                invalid={
                                  errors[`stock_events[${index}].quantity`]
                                }
                                invalidText={
                                  errors[`stock_events[${index}].quantity`]
                                    ?.message
                                }
                                measureUnit={watch(
                                  `stock_events[${index}].measure_unit`
                                )}
                              />
                            );
                          }}
                          control={control}
                          name={`stock_events[${index}].quantity`}
                        />

                        <Controller
                          name={`stock_events[${index}].measure_unit`}
                          control={control}
                        />
                      </RepeaterItem>
                    ))}
                  </div>
                  <RepeaterAddButton
                    onClick={() => append({ name: "commodities" })}
                  />
                </>
              )}
              {meal_provided && (
                <div className="replicate-meal-section">
                  <label className="wfp--label replicate-meal-label">
                    <FormattedMessage
                      id="MealsEdit.replicateMealLabel"
                      defaultMessage="Replicate meal from the last school day"
                    />
                  </label>
                  <div className="replicate-section-buttons">
                    <ButtonRouter
                      disabled={params.details.includes(
                        "prepopulate-per-student"
                      )}
                      to={buildAttendanceUrl(
                        currentDate,
                        params.schoolId,
                        params.item,
                        params.thrItem,
                        params.studentItem,
                        `${currentParam}-prepopulate-per-student`
                      )}
                    >
                      <FormattedMessage
                        id="MealsEdit.perStudent"
                        defaultMessage="Per student"
                      />
                    </ButtonRouter>
                    <ButtonRouter
                      disabled={params.details.includes(
                        "prepopulate-total-quantity"
                      )}
                      to={buildAttendanceUrl(
                        currentDate,
                        params.schoolId,
                        params.item,
                        params.thrItem,
                        params.studentItem,
                        `${currentParam}-prepopulate-total-quantity`
                      )}
                    >
                      <FormattedMessage
                        id="MealsEdit.totalQuantity"
                        defaultMessage="Total quantity"
                      />
                    </ButtonRouter>
                  </div>
                  {!previousAttendanceStoreWithConsumptionData &&
                    (prepopulatePerStudent || prepopulateTotalQuantity) && (
                      <Blockquote kind="warning">
                        <FormattedMessage
                          id="MealsEdit.noPreviousDataToImport"
                          defaultMessage="There is no previous data to import."
                        />
                      </Blockquote>
                    )}
                  {previousAttendanceStoreWithConsumptionData &&
                    prepopulatePerStudent &&
                    !previousAttendanceStoreWithConsumptionData.levels && (
                      <Blockquote kind="warning">
                        <FormattedMessage
                          id="MealsEdit.noPreviousAttendance"
                          defaultMessage="There is no attendance information in the previous day, therefore quantity per student cannot be calculated."
                        />
                      </Blockquote>
                    )}
                </div>
              )}
            </div>
          </ModalExtended>
          <button className="hidden-btn" ref={submitBtnRef as any} />
        </form>
      </FormProvider>
    );
  }
);

const MealsEdit = ({
  currentStoreData,
  currentStudentStoreData,
  previousAttendanceStoreWithConsumptionData,
  prepopulatePerStudent,
  prepopulateTotalQuantity,
}: any) => {
  const params: any = useParams();
  const commoditiesIndex = useSelector(commoditiesDuck.getIndex);

  const stockStatus = useSelector(
    getStockStatus(moment().format("YYYY-MM-DD"))
  );
  const currentSchoolProfile = useSelector(getCurrentSchoolProfile);
  const commoditiesListWfpFromSchoolProfile = useSelector(
    getCurrentSchoolProfileWfpCommodities
  );
  const commoditiesListFromSchoolProfile = useSelector(
    getCurrentSchoolProfileCommodities
  );
  const latestSchoolYear = useSelector(getCurrentOrLatestSchoolYear);
  // Get all the attendances store objects for the report's month
  const attendances = useSelector(
    getDeliveryAttendancesByDateRange(
      latestSchoolYear?.starts_on!,
      latestSchoolYear?.ends_on!
    )
  );
  const studentAttendances = useSelector(
    getStudentAttendancesByDateRange(
      latestSchoolYear?.starts_on!,
      latestSchoolYear?.ends_on!
    )
  );

  const isSchoolsFetching = useSelector(getIsSchoolsFetching);
  const isAllCommoditySourcesFetching = useSelector(
    getAllCommoditySourcesFetching
  );

  const allCommoditySources: CommoditySource[] =
    useSelector(getAllCommoditySources) || [];

  const isOffline = useSelector(getLoggedInOffline);

  // Are we still fetching some data
  const isFetching = useMemo(
    () =>
      isSchoolsFetching ||
      isAllCommoditySourcesFetching ||
      allCommoditySources.length === 0,
    [
      isSchoolsFetching,
      isAllCommoditySourcesFetching,
      allCommoditySources.length,
    ]
  );

  const dispatch = useDispatch();

  useEffect(() => {
    // If we are on offline mode,
    // Set schools.commoditysources.data from schools.userCommoditySources.data.
    // If not get request commoditysources from the server for current school.
    if (isOffline) {
      dispatch(setCommoditySourcesFromSchoolCache(params.schoolId));
    } else {
      dispatch(requestAllCommoditySources());
      dispatch(requestCommoditySourcesForSchool(params.schoolId));
    }
  }, [dispatch, isOffline, params.schoolId]);

  if (isFetching) {
    return <Loading with_overlay={true} />;
  }

  return (
    <MealsEditModal
      currentStoreData={currentStoreData}
      currentStudentStoreData={currentStudentStoreData}
      previousAttendanceStoreWithConsumptionData={
        previousAttendanceStoreWithConsumptionData
      }
      prepopulatePerStudent={prepopulatePerStudent}
      prepopulateTotalQuantity={prepopulateTotalQuantity}
      commoditiesIndex={commoditiesIndex}
      latestStockStatus={stockStatus}
      currentSchoolProfile={currentSchoolProfile}
      commoditiesListWfpFromSchoolProfile={commoditiesListWfpFromSchoolProfile}
      commoditiesListFromSchoolProfile={commoditiesListFromSchoolProfile}
      latestSchoolYear={latestSchoolYear}
      attendances={attendances}
      studentAttendances={studentAttendances}
      allCommoditySources={allCommoditySources}
    ></MealsEditModal>
  );
};

export default MealsEdit;
